import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Check, X, MessageCircle, AlertTriangle, RefreshCw, BedDouble, Users, Phone, Mail, Plane, Briefcase } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending:            "bg-amber-100 text-amber-900 border-amber-200",
  pending_department: "bg-purple-100 text-purple-900 border-purple-200",
  pending_cmo:        "bg-indigo-100 text-indigo-900 border-indigo-200",
  needs_info: "bg-blue-100 text-blue-900 border-blue-200",
  approved:   "bg-emerald-100 text-emerald-900 border-emerald-200",
  booked:     "bg-[#E9F5DB] text-[#1B4332] border-[#1B4332]/20",
  declined:   "bg-red-100 text-red-900 border-red-200",
};
const STATUS_LABELS = {
  pending: "Pending review",
  pending_department: "Awaiting dept head",
  pending_cmo: "Awaiting CMO",
  needs_info: "Needs info",
  approved: "Approved",
  booked: "Booked",
  declined: "Declined",
};

export default function GuestRequestDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const canManage = hasPerm("guest_requests.manage");
  const canDeptApprove = hasPerm("requests.approve_dept") || hasPerm("requests.approve_dept_any");
  const canCmoApprove = hasPerm("requests.approve_cmo");
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [units, setUnits] = useState([]);
  const [rateCards, setRateCards] = useState([]);

  const [actionDialog, setActionDialog] = useState(null); // 'approve' | 'decline' | 'request_info' | 'respond' | 'convert'
  const [actionMsg, setActionMsg] = useState("");
  const [convertForm, setConvertForm] = useState({ unit_id: "", rate_per_night: 0, rate_currency: "KES", rate_card_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/guest-requests/${id}`);
      setReq(data);
      if (data.preferred_unit_id) {
        setConvertForm((f) => ({ ...f, unit_id: data.preferred_unit_id }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
      navigate("/guest-requests");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    api.get("/housing/units").then(({ data }) => setUnits((data || []).filter((u) => u.active))).catch(() => {});
    api.get("/housing/rate-cards").then(({ data }) => setRateCards((data || []).filter((c) => c.active))).catch(() => {});
  }, [canManage]);

  const openDialog = (kind) => {
    setActionMsg(kind === "approve" ? "" : "");
    setActionDialog(kind);
  };

  const runAction = async () => {
    if (!req) return;
    setBusy(true);
    try {
      if (actionDialog === "approve") {
        await api.post(`/guest-requests/${id}/approve`, { message: actionMsg });
        toast.success("Approved — submitter has been notified");
      } else if (actionDialog === "decline") {
        await api.post(`/guest-requests/${id}/decline`, { message: actionMsg });
        toast.success("Declined — submitter has been notified");
      } else if (actionDialog === "request_info") {
        if (!actionMsg.trim()) { setBusy(false); return toast.error("Type the question for the submitter"); }
        await api.post(`/guest-requests/${id}/request-info`, { message: actionMsg });
        toast.success("Info request sent");
      } else if (actionDialog === "respond") {
        if (!actionMsg.trim()) { setBusy(false); return toast.error("Type your answer"); }
        await api.post(`/guest-requests/${id}/respond-info`, { message: actionMsg });
        toast.success("Response sent — the coordinator will review");
      } else if (actionDialog === "dept_approve" || actionDialog === "dept_decline") {
        await api.post(`/guest-requests/${id}/department-approval`, {
          action: actionDialog === "dept_approve" ? "approve" : "decline", notes: actionMsg,
        });
        toast.success(actionDialog === "dept_approve" ? "Approved — sent to CMO" : "Declined — submitter has been notified");
      } else if (actionDialog === "cmo_approve" || actionDialog === "cmo_decline") {
        await api.post(`/guest-requests/${id}/cmo-approval`, {
          action: actionDialog === "cmo_approve" ? "approve" : "decline", notes: actionMsg,
        });
        toast.success(actionDialog === "cmo_approve" ? "Approved — sent to the hospitality coordinator" : "Declined — submitter has been notified");
      } else if (actionDialog === "convert") {
        if (!convertForm.unit_id) { setBusy(false); return toast.error("Pick a housing unit first"); }
        const card = rateCards.find((c) => c.id === convertForm.rate_card_id);
        const { data } = await api.post(`/guest-requests/${id}/convert-to-booking`, {
          unit_id: convertForm.unit_id,
          rate_per_night: Number(convertForm.rate_per_night) || 0,
          rate_currency: convertForm.rate_currency,
          rate_card_id: convertForm.rate_card_id || null,
          rate_card_name: card?.name || "",
          notes: actionMsg,
        });
        toast.success("Booking created — opening it now");
        setActionDialog(null);
        setBusy(false);
        navigate(`/bookings?edit=${data.booking.id}`, { state: { from: "/guest-requests" } });
        return;
      }
      setActionDialog(null);
      setActionMsg("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      await api.post(`/guest-requests/${id}/recheck-conflicts`, {});
      toast.success("Conflict check refreshed");
      load();
    } catch {
      toast.error("Recheck failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !req) {
    return <div className="py-20 text-center text-[#7A8A84]">Loading…</div>;
  }

  const isSubmitter = req.submitted_by_user_id === user.id;
  const preferredUnitName = units.find((u) => u.id === req.preferred_unit_id)?.name;

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="guest-request-detail-page">
      <Button variant="ghost" onClick={() => navigate("/guest-requests")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Guest Requests
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              {req.guest_name}
            </h1>
            <Badge variant="outline" className={STATUS_STYLES[req.status] || ""} data-testid="gr-status-badge">
              {STATUS_LABELS[req.status] || req.status}
            </Badge>
          </div>
          <p className="text-[#525C58] mt-1">
            {req.arrival_date} → {req.departure_date} · {req.num_guests} guest{req.num_guests === 1 ? "" : "s"}
            {req.relationship && <> · {req.relationship}</>}
          </p>
          <p className="text-xs text-[#7A8A84] mt-1">
            Submitted by {req.submitted_by_name} ({req.submitted_by_role}) on {new Date(req.created_at).toLocaleString()}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {req.status === "pending_department" && canDeptApprove && (
            <>
              <Button variant="outline" className="border-red-300 text-red-600" onClick={() => openDialog("dept_decline")} data-testid="gr-dept-decline-btn">
                <X className="h-4 w-4 mr-2" /> Decline (dept)
              </Button>
              <Button onClick={() => openDialog("dept_approve")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-dept-approve-btn">
                <Check className="h-4 w-4 mr-2" /> Approve (dept)
              </Button>
            </>
          )}
          {req.status === "pending_cmo" && canCmoApprove && (
            <>
              <Button variant="outline" className="border-red-300 text-red-600" onClick={() => openDialog("cmo_decline")} data-testid="gr-cmo-decline-btn">
                <X className="h-4 w-4 mr-2" /> Decline (CMO)
              </Button>
              <Button onClick={() => openDialog("cmo_approve")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-cmo-approve-btn">
                <Check className="h-4 w-4 mr-2" /> Approve (CMO)
              </Button>
            </>
          )}
          {canManage && req.status === "pending" && (
            <>
              <Button variant="outline" onClick={() => openDialog("request_info")} data-testid="gr-request-info-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> Request info
              </Button>
              <Button variant="outline" className="border-red-300 text-red-600" onClick={() => openDialog("decline")} data-testid="gr-decline-btn">
                <X className="h-4 w-4 mr-2" /> Decline
              </Button>
              <Button onClick={() => openDialog("approve")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-approve-btn">
                <Check className="h-4 w-4 mr-2" /> Approve
              </Button>
            </>
          )}
          {canManage && req.status === "needs_info" && (
            <Button variant="outline" onClick={() => openDialog("request_info")} data-testid="gr-ask-again-btn">
              <MessageCircle className="h-4 w-4 mr-2" /> Ask again
            </Button>
          )}
          {canManage && req.status === "approved" && (
            <Button onClick={() => openDialog("convert")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-convert-btn">
              <BedDouble className="h-4 w-4 mr-2" /> Create booking
            </Button>
          )}
          {canManage && req.status === "booked" && req.booking_id && (
            <Button variant="outline" onClick={() => navigate(`/bookings?edit=${req.booking_id}`, { state: { from: "/guest-requests" } })} data-testid="gr-open-booking-btn">
              <BedDouble className="h-4 w-4 mr-2" /> Open booking
            </Button>
          )}
          {isSubmitter && req.status === "needs_info" && (
            <Button onClick={() => openDialog("respond")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-respond-btn">
              <MessageCircle className="h-4 w-4 mr-2" /> Reply with info
            </Button>
          )}
        </div>
      </div>

      {/* Conflict warning */}
      {req.conflict_check?.has_conflict && req.status !== "declined" && req.status !== "booked" && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-amber-900 font-semibold">
                <AlertTriangle className="h-4 w-4" />
                Date clash detected
              </div>
              {canManage && (
                <Button variant="ghost" size="sm" onClick={recheck} disabled={busy} className="text-amber-900">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recheck
                </Button>
              )}
            </div>
            {(req.conflict_check.bookings || []).length > 0 && (
              <div className="text-sm text-amber-900">
                <strong>Bookings:</strong>{" "}
                {req.conflict_check.bookings.map((b) => `${b.visitor_name} (${b.from_date}→${b.to_date})`).join(", ")}
              </div>
            )}
            {(req.conflict_check.leases || []).length > 0 && (
              <div className="text-sm text-amber-900">
                <strong>Leases:</strong>{" "}
                {req.conflict_check.leases.map((l) => `${l.tenant_name} (${l.start_date}→${l.end_date || "open-ended"})`).join(", ")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hospital work approval chain */}
      {req.hospital_work && (
        <Card className="border-[#E8E8E6]" data-testid="gr-work-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-[#1B4332]" /> Hospital work participation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div><strong>Department:</strong> {req.work_department || "—"}</div>
            {req.work_comments && (
              <div className="text-[#525C58]"><strong>Explanation:</strong> {req.work_comments}</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              {[
                { label: `Department head (${req.work_department})`, ap: req.work_department_approval, activeWhen: "pending_department" },
                { label: "CMO", ap: req.work_cmo_approval, activeWhen: "pending_cmo" },
              ].map((step, i) => {
                const st = step.ap?.status || "pending";
                const chip = st === "approved" ? "bg-emerald-100 text-emerald-900 border-emerald-200"
                  : st === "declined" ? "bg-red-100 text-red-900 border-red-200"
                  : req.status === step.activeWhen ? "bg-amber-100 text-amber-900 border-amber-200"
                  : "bg-gray-100 text-gray-600 border-gray-200";
                return (
                  <div key={i} className="p-3 rounded-lg border border-[#E8E8E6] bg-[#FAFAF8]" data-testid={`gr-work-step-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-[#1C1F1E]">{step.label}</span>
                      <Badge variant="outline" className={chip}>
                        {st === "pending" && req.status === step.activeWhen ? "Awaiting" : st}
                      </Badge>
                    </div>
                    {step.ap?.approved_by_name && (
                      <div className="text-xs text-[#7A8A84] mt-1">
                        {step.ap.approved_by_name} · {step.ap.approved_at ? new Date(step.ap.approved_at).toLocaleString() : ""}
                      </div>
                    )}
                    {step.ap?.notes && <div className="text-xs text-[#525C58] mt-1">{step.ap.notes}</div>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Guest details card */}
      <Card className="border-[#E8E8E6]">
        <CardHeader><CardTitle className="text-lg">Guest details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#7A8A84]" />{req.guest_name}</div>
          {req.guest_phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#7A8A84]" />{req.guest_phone}</div>}
          {req.guest_email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#7A8A84]" />{req.guest_email}</div>}
          {preferredUnitName && <div className="flex items-center gap-2"><BedDouble className="h-4 w-4 text-[#7A8A84]" />Suggested: {preferredUnitName}</div>}
          {req.purpose && <div className="md:col-span-2"><strong>Purpose:</strong> {req.purpose}</div>}
        </CardContent>
      </Card>

      {/* Transport */}
      {req.transport?.needed && (
        <Card className="border-[#E8E8E6]">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plane className="h-4 w-4" /> Airport transport</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {req.transport.inbound?.flight_number && (
              <div><strong>Inbound:</strong> {req.transport.inbound.flight_number} · {req.transport.inbound.date} {req.transport.inbound.time}</div>
            )}
            {req.transport.outbound?.flight_number && (
              <div><strong>Outbound:</strong> {req.transport.outbound.flight_number} · {req.transport.outbound.date} {req.transport.outbound.time}</div>
            )}
            {req.transport.notes && <div className="md:col-span-2 text-[#525C58]">{req.transport.notes}</div>}
          </CardContent>
        </Card>
      )}

      {/* Info thread */}
      {(req.info_thread || []).length > 0 && (
        <Card className="border-[#E8E8E6]">
          <CardHeader><CardTitle className="text-lg">Conversation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {req.info_thread.map((entry, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${entry.kind === "question" ? "bg-blue-50 border-blue-200" : "bg-[#F4F4F1] border-[#E8E8E6]"}`} data-testid={`thread-entry-${idx}`}>
                <div className="text-xs text-[#7A8A84] mb-1">
                  {entry.kind === "question" ? "🛎 " : "✉ "}
                  <strong>{entry.by_name}</strong> · {new Date(entry.at).toLocaleString()}
                </div>
                <div className="text-sm text-[#1C1F1E] whitespace-pre-wrap">{entry.message}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Coordinator note / decline reason */}
      {req.coordinator_note && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 text-sm text-emerald-900">
            <strong>Coordinator note:</strong> {req.coordinator_note}
          </CardContent>
        </Card>
      )}
      {req.decline_reason && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-900">
            <strong>Decline reason:</strong> {req.decline_reason}
          </CardContent>
        </Card>
      )}

      {/* ───── Action dialog ───── */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent data-testid="gr-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog === "approve" && "Approve guest request"}
              {actionDialog === "decline" && "Decline guest request"}
              {actionDialog === "request_info" && "Request more info"}
              {actionDialog === "respond" && "Reply to coordinator"}
              {actionDialog === "convert" && "Create booking"}
              {actionDialog === "dept_approve" && "Approve — department head"}
              {actionDialog === "dept_decline" && "Decline — department head"}
              {actionDialog === "cmo_approve" && "Approve — CMO"}
              {actionDialog === "cmo_decline" && "Decline — CMO"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog === "approve" && "Optional note to the submitter."}
              {actionDialog === "decline" && "Tell the submitter why so they can adjust and resubmit."}
              {actionDialog === "request_info" && "What additional info do you need from the submitter?"}
              {actionDialog === "respond" && "Provide the information the coordinator asked for."}
              {actionDialog === "convert" && "Assign a unit + rate. The booking opens in draft so you can finalise add-ons."}
              {actionDialog === "dept_approve" && "Approving sends this request to the CMO for the next approval step."}
              {actionDialog === "dept_decline" && "Declining closes the request and notifies the submitter."}
              {actionDialog === "cmo_approve" && "Approving sends this request to the hospitality coordinator for housing review."}
              {actionDialog === "cmo_decline" && "Declining closes the request and notifies the submitter."}
            </DialogDescription>
          </DialogHeader>

          {actionDialog === "convert" ? (
            <div className="space-y-3">
              <div>
                <Label>Housing unit *</Label>
                <Select value={convertForm.unit_id} onValueChange={(v) => setConvertForm((f) => ({ ...f, unit_id: v }))}>
                  <SelectTrigger data-testid="gr-convert-unit"><SelectValue placeholder="Select a unit" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} (cap {u.capacity})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate / night</Label>
                  <Input type="number" min="0" data-testid="gr-convert-rate"
                    value={convertForm.rate_per_night}
                    onChange={(e) => setConvertForm((f) => ({ ...f, rate_per_night: e.target.value }))} />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={convertForm.rate_currency} onValueChange={(v) => setConvertForm((f) => ({ ...f, rate_currency: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {rateCards.length > 0 && (
                <div>
                  <Label>Rate card (optional)</Label>
                  <Select value={convertForm.rate_card_id || "none"} onValueChange={(v) => {
                    if (v === "none") { setConvertForm((f) => ({ ...f, rate_card_id: "" })); return; }
                    const card = rateCards.find((c) => c.id === v);
                    setConvertForm((f) => ({
                      ...f,
                      rate_card_id: v,
                      rate_per_night: card?.amount ?? f.rate_per_night,
                      rate_currency: card?.currency ?? f.rate_currency,
                    }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {rateCards.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} · {c.currency} {c.amount}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Booking notes (optional)</Label>
                <Textarea rows={2} value={actionMsg} onChange={(e) => setActionMsg(e.target.value)} />
              </div>
            </div>
          ) : (
            <Textarea
              data-testid="gr-action-msg"
              rows={4}
              value={actionMsg}
              onChange={(e) => setActionMsg(e.target.value)}
              placeholder={
                actionDialog === "approve" ? "e.g. We've assigned Cottage 2." :
                actionDialog === "decline" ? "e.g. No availability that week — please try Aug 1–5." :
                actionDialog === "request_info" ? "e.g. Could you confirm the guest's relationship and dietary needs?" :
                actionDialog === "dept_approve" || actionDialog === "cmo_approve" ? "Optional note…" :
                actionDialog === "dept_decline" || actionDialog === "cmo_decline" ? "Reason for declining…" :
                "Type your response..."
              }
            />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={busy}>Cancel</Button>
            <Button onClick={runAction} disabled={busy} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-action-confirm-btn">
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
